import { useEffect, useState } from "react";

function useReducedMotion() {
  const [r, setR] = useState(() => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches);
  useEffect(() => {
    const m = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    if (!m) return undefined;
    const on = () => setR(m.matches);
    m.addEventListener("change", on);
    return () => m.removeEventListener("change", on);
  }, []);
  return r;
}

const Cloud = ({ x, y, s = 1, fill, cls, delay = 0 }) => (
  <g className={cls} style={{ animationDelay: `${delay}s` }}>
    <g transform={`translate(${x} ${y}) scale(${s})`}>
      <path d="M10 30a12 12 0 0 1 2-23.8A16 16 0 0 1 42 9a11 11 0 0 1 2 21.8Z" fill={fill} />
    </g>
  </g>
);

function Sun({ cx = 60, cy = 52, r = 18 }) {
  return (
    <g>
      <g className="wx-spin" style={{ transformOrigin: `${cx}px ${cy}px` }}>
        {Array.from({ length: 10 }, (_, i) => <rect key={i} x={cx - 2} y={cy - r - 14} width="4" height="9" rx="2" fill="#FDE68A" transform={`rotate(${i * 36} ${cx} ${cy})`} />)}
      </g>
      <circle cx={cx} cy={cy} r={r} fill="url(#wxSun)" className="wx-pulse" style={{ transformOrigin: `${cx}px ${cy}px` }} />
    </g>
  );
}

function Moon({ cx = 62, cy = 50 }) {
  return (
    <g>
      {[[20, 22], [96, 18], [88, 44], [28, 60], [106, 70], [48, 14]].map(([x, y], i) => <circle key={i} cx={x} cy={y} r={i % 2 ? 1.4 : 1} fill="#fff" className="wx-twinkle" style={{ animationDelay: `${i * 0.4}s` }} />)}
      <path d={`M${cx + 6} ${cy - 20}a20 20 0 1 0 14 30a16 16 0 0 1-14-30Z`} fill="#E5E7EB" />
    </g>
  );
}

const Drops = ({ n, snow, light }) => (
  <g>
    {Array.from({ length: n }, (_, i) => {
      const x = 22 + ((i * 37) % 78);
      const delay = ((i * 0.23) % 1.2).toFixed(2);
      return snow
        ? <circle key={i} cx={x} cy={70} r="2.4" fill="#fff" className="wx-snow" style={{ animationDelay: `${delay * 2}s` }} />
        : <line key={i} x1={x} y1={72} x2={x - 3} y2={light ? 78 : 82} stroke="#93C5FD" strokeWidth={light ? 1.6 : 2.2} strokeLinecap="round" className="wx-rain" style={{ animationDelay: `${delay}s` }} />;
    })}
  </g>
);

export default function WeatherScene({ condition = "clear", isDay = true, size = 120, still: forceStill, testid }) {
  const reduced = useReducedMotion();
  const still = forceStill || reduced;
  const grey = condition === "thunder" ? "#6B7280" : isDay ? "#F3F4F6" : "#9CA3AF";
  const back = isDay ? "#E5E7EB" : "#6B7280";
  const showSun = ["clear", "partly-cloudy"].includes(condition);
  return (
    <svg viewBox="0 0 120 110" width={size} height={size * 0.92} className={`wx ${still ? "wx-still" : ""}`} data-testid={testid} data-condition={condition} data-day={isDay ? "1" : "0"} data-still={still ? "1" : "0"} aria-label={condition} role="img">
      <defs><radialGradient id="wxSun"><stop offset="0%" stopColor="#FEF3C7" /><stop offset="100%" stopColor="#F59E0B" /></radialGradient></defs>
      {showSun && (isDay ? <Sun cx={condition === "clear" ? 60 : 48} cy={condition === "clear" ? 52 : 42} r={condition === "clear" ? 20 : 15} /> : <Moon cx={condition === "clear" ? 56 : 44} cy={condition === "clear" ? 52 : 40} />)}
      {condition === "partly-cloudy" && <Cloud x={44} y={44} s={1.3} fill={grey} cls="wx-drift" />}
      {["cloudy", "rain", "drizzle", "thunder", "snow"].includes(condition) && (
        <>
          <Cloud x={14} y={26} s={1.15} fill={back} cls="wx-drift-slow" />
          <Cloud x={40} y={34} s={1.4} fill={grey} cls="wx-drift" delay={-2} />
        </>
      )}
      {condition === "rain" && <Drops n={9} />}
      {condition === "drizzle" && <Drops n={6} light />}
      {condition === "snow" && <Drops n={8} snow />}
      {condition === "thunder" && (<><Drops n={6} /><path d="M64 70 54 88h8l-5 16 16-22h-9l6-12Z" fill="#FDE047" className="wx-flash" /></>)}
      {condition === "fog" && [36, 52, 68, 84].map((y, i) => <rect key={y} x={14 + (i % 2) * 8} y={y} width={84} height={6} rx={3} fill={isDay ? "#E5E7EB" : "#9CA3AF"} opacity={0.85 - i * 0.12} className="wx-fog" style={{ animationDelay: `${i * -1.5}s` }} />)}
    </svg>
  );
}
